import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Modal, notify } from '../../../../components';
import Icon from '../../../../components/icon/icon';
import { Envelope, X } from '@phosphor-icons/react';
import VerifyTicket from './verifyTicket';
import TicketVerified from './ticketVerified';
import VerificationFailModal from './verificationFailModal';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { convertCustomDateFormatToISO } from '../../../../utils/commonUtils';
import { notifyTeam } from '../../api';
import mixpanel from 'mixpanel-browser';

const ForwardModal = ({
  isOpen,
  onClose,
  proceedToStep3,
  selectedPlatform,
}) => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tryAgainClicks, setTryAgainClicks] = useState(0);

  const navigate = useNavigate();

  const { userData } = useSelector((state) => state.app);
  const { eventData, verificationData } = useSelector((state) => state.sellTickets);

  const handleError = (error) => {
    let title = '';
    let content = '';
    let cta = '';

    if (error?.includes('Your Email Address verification failed.')) {
      title =
      'Please make sure that the email address you used to purchase the tickets is the same you\'ve used to log in to our website.';
      content =
      `Make sure you share tickets for “${eventData?.name}” or select a different event in the previous step.`;
      cta = 'Try again';
    } else if (error?.includes('List the tickets to correct event')) {
      title = 'Forward tickets for the event you selected';
      content =
          `Please share tickets for “${eventData?.name}” or select a different event in the previous step.`;
      cta = 'Try again';
    } else if (error?.includes('Ticket Verification Failed')) {
      title = 'We’re not able to verify your tickets automatically!';
      content =
          'Please notify us and we’ll get your tickets verified within 24 hours. ';
      cta = 'Notify the team!';
    } else if (error?.includes('No Email Forwarded')) {
      title = 'We didn’t get your tickets';
      content = `Please forward the confirmation email again from ${userData?.email}`;
      cta = 'Try again';
    } else if (error?.includes('Ticket Already Exists')) {
      title = 'These tickets have already been listed';
      content =
          'Please list a different ticket as these have already been listed.';
      cta = 'Edit Listing';
    } else if (error?.includes('Ticket date does not match the event date')) {
      title = 'Data mismatch';
      content =
          `The ticket details don’t match the selected event. Please check that you’ve chosen the correct event, date, and location, then try again.`;
      cta = 'Try again';
    } else {
      title = 'We’re not able to verify your tickets automatically!';
      content =
          'Please notify us and we’ll get your tickets verified within 24 hours. ';
      cta = 'Notify the team!';
    }

    mixpanel.track("Ticket_Verification_Failed", {
        "event_id": eventData?._id,
        "event_name": eventData?.name,
        "user_id": userData._id,
        "ticket_status": title,
        "step_status": "Error",
        "page_url": window.location.href,
      });

    setErrorMessage({ title, content, cta });
    setShowFailModal(true);
  };

  const handleVerificationComplete = (data) => {
    setShowVerifyModal(false);
    if (data?.verificationId) {
      mixpanel.track("Ticket_Verification_Completed", {
        "event_id": eventData?._id,
        "event_name": eventData?.name,
        "user_id": userData._id,
        "verification_status": "Success",
        "page_url": window.location.href,
      });
      setVerificationComplete(true);
    }
    else handleError(typeof data == 'string' ?  data : (data?.verificationComments || data?.msg));
  };

  useEffect(() => {
    if (verificationComplete) {
      const timer = setTimeout(() => {
        setVerificationComplete(false);
        proceedToStep3();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [verificationComplete, proceedToStep3]);

  const handleClick = () => {
    setShowVerifyModal(true);
    setShowFailModal(false);
  };

  const handleTryAgain = () => {
    if (tryAgainClicks < 2) {
      setTryAgainClicks(tryAgainClicks + 1);
      mixpanel.track("Ticket_Forwarded", {
        "event_id": eventData?._id,
        "event_name": eventData?.name,
        "user_id": userData._id,
        "ticket_status": "Email Sent",
        "step_status": "Forwarded Email",
        "page_url": window.location.href,
      });
    } else {
      setErrorMessage({
        title: 'We’re not able to verify your tickets automatically!',
        content: 'Please notify us and we’ll get your tickets verified within 24 hours. ',
        cta: 'Notify the team!',
      });
      setShowFailModal(true);
      return;
    }
    handleClick(tryAgainClicks + 1);
  };

  const onCtaClick = () => {
    if (errorMessage?.cta === 'Try again') {
      setShowFailModal(false);
      handleTryAgain();
    } else if (errorMessage?.cta === 'Notify the team!') {
      notifyTeam({
        eventId: eventData?._id,
        platform: selectedPlatform?.label,
      }, () => {
        notify('success', 'Team Notified');
        navigate('/');
      });
    } else if (errorMessage?.cta === 'Edit Listing') {
      navigate('/my-listing');
    } else {
      setShowFailModal(false);
      onClose();
    }
  };

  return (
    <div>
      <Helmet>
        <title>Verify tickets</title>
        <meta
          name="Sell Tickets Easily in India | Reach Your Audience on Take My Tickets"
          content="Start selling your event tickets hassle-free on Take My Tickets, India's trusted ticket resale platform. Add seller contact details effortlessly."
        />
        <meta charset="UTF-8" />
      </Helmet>
      {showFailModal ? (
        <VerificationFailModal
          isOpen={showFailModal}
          onClose={() => {
            setShowFailModal(false);
            onClose();
          }}
          title={errorMessage.title}
          content={errorMessage.content}
          cta={errorMessage.cta}
          onCtaClick={onCtaClick}
        />
      ) : (
        !showVerifyModal &&
        !verificationComplete && (
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="regular"
            className="forward-modal"
          >
            <div className="flex justify-between items-center">
              <Icon type="square">
                <Envelope size={32}
                  color="#101010" />
              </Icon>
              <Icon type="circle"
                onClick={onClose}>
                <X size={24} />
              </Icon>
            </div>
            <div className="md:heading-24 heading-16 md:pt-6 pt-5 pb-3">
              Did you forward the ticket email to tickets@takemytickets.co.in?
            </div>
            <div className="md:text-16">
              By forwarding the email, you acknowledge and consent to the
              verification process.
            </div>
            <div className="flex flex-col items-center justify-center">
              <Button
                type="primary"
                onClick={handleTryAgain}
                className="mt-5 mb-3"
              >
                I’ve forwarded my tickets!
              </Button>
              <div
                className="text-primary underline heading-16 cursor-pointer"
                onClick={onClose}
              >
                See how to forward
              </div>
            </div>
          </Modal>
        )
      )}
      {showVerifyModal && !verificationComplete && (
        <VerifyTicket
          onVerified={handleVerificationComplete}
          tryAgainClicks={tryAgainClicks}
          payload={{
            eventID: eventData?._id,
            userID: userData?._id,
            platform: selectedPlatform?.label,
          }}
        />
      )}
      {verificationComplete && (
        <TicketVerified
          ticketImg={eventData?.image}
          eventName={eventData?.name}
          eventDate={format(convertCustomDateFormatToISO(eventData?.start_date), 'E, d LLL yyyy')}
          eventTime={format(convertCustomDateFormatToISO(eventData?.start_date), 'hh:mm a')}
          eventLocation={eventData?.venue}
          ticketCount={verificationData?.quantity}
          totalAmt={verificationData?.totalPrice}
        />
      )}
    </div>
  );
};

export default ForwardModal;
